import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgxFileDropEntry, FileSystemFileEntry, NgxFileDropModule } from 'ngx-file-drop';
import { CommonModule } from '@angular/common';
import { FileService } from 'src/app/services/file/file.service';

@Component({
  selector: 'app-upload',
  imports: [NgxFileDropModule, CommonModule],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.css'
})
export class UploadComponent {
  uploadedImages: string[] = [];
  uploadProgress = 0;
  dragActive = false;
  droppedFileNames: string[] = [];
  private pendingUploads = 0;

  @Output() uploaded = new EventEmitter<any>(); // emits backend response per file
  @Output() uploadComplete = new EventEmitter<void>();
  @Input() uploadPath: string = this.getDefaultPath();

  constructor(private fileService: FileService) {}

  private getDefaultPath(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `res/images/${year}/${month}`;
  }

  public dropped(files: NgxFileDropEntry[]) {
    this.pendingUploads += files.length;
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.droppedFileNames.push(file.name);
          this.fileService.uploadFile(file, this.uploadPath).subscribe({
            next: (event: any) => {
              if (event.type === 1 && event.total) {
                this.uploadProgress = Math.round((event.loaded / event.total) * 100);
              } else if (event.type === 4) {
                const response = event.body as any;
                // backend returns { new: { url }, old: { url } }
                this.uploadedImages.push(response?.new?.url || response?.url);
                this.uploaded.emit(response);
                this.pendingUploads--;
                if (this.pendingUploads === 0) {
                  this.uploadComplete.emit();
                  this.droppedFileNames = [];
                  this.uploadProgress = 0;
                }
              }
            },
            error: (err: any) => {
              console.error('Upload error:', err);
              this.pendingUploads--;
              if (this.pendingUploads === 0) {
                this.uploadComplete.emit();
                this.droppedFileNames = [];
                this.uploadProgress = 0;
              }
            },
          });
        });
      } else {
        this.pendingUploads--;
        if (this.pendingUploads === 0) {
          this.uploadComplete.emit();
          this.droppedFileNames = [];
          this.uploadProgress = 0;
        }
      }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    // Clear previous file names before adding new ones
    this.droppedFileNames = [];
    this.pendingUploads += input.files.length;
    for (const file of Array.from(input.files)) {
      this.droppedFileNames.push(file.name);

      this.fileService.uploadFile(file, this.uploadPath).subscribe({
        next: (event: any) => {
          if (event.type === 1 && event.total) {
            this.uploadProgress = Math.round((event.loaded / event.total) * 100);
          } else if (event.type === 4) {
            const response = event.body as any;
            // backend returns { new: { url }, old: { url } }
            this.uploadedImages.push(response?.new?.url || response?.url);
            this.uploaded.emit(response);
            this.pendingUploads--;
            if (this.pendingUploads === 0) {
              this.uploadProgress = 0;
              this.uploadComplete.emit();
              this.droppedFileNames = [];
            }
          }
        },
        error: (err: any) => {
          console.error('Upload error:', err);
          this.pendingUploads--;
          if (this.pendingUploads === 0) {
            this.uploadProgress = 0;
            this.uploadComplete.emit();
            this.droppedFileNames = [];
          }
        }
      });
    }

    // Reset input so the same file can be selected again
    input.value = '';
  }
}
